import { gettext } from '@eventbrite/legacy-infra.i18n';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { UserContext } from '../context';
import { isValidEmail } from '../util';

interface Field {
    value?: string;
    error: string;
}

/**
 * @name useForm
 * @description custom hook exposing functionality required by report event form
 * @returns:
 * - fields {object}: a representation of field state, errors and requirements
 * - helpers {object}: contains `useState`-derived setter methods to manage form state and form validation
 */
export const useForm = () => {
    const user = useContext(UserContext);
    const [email, setEmail] = useState<Field>({
        value: user?.primaryEmail,
        error: '',
    });
    const [description, setDescription] = useState<Field>({
        value: '',
        error: '',
    });
    const [required, setRequired] = useState<{
        email: boolean;
        description: boolean;
    }>({
        email: true,
        description: true,
    });

    const validateField = (field: string) => {
        const validate: {
            [key: string]: { result: boolean; message: string };
        } = {
            email: {
                result:
                    required.email &&
                    (!isValidEmail(email.value) || email.value === ''),
                message: gettext('Email is empty or invalid.').toString(),
            },
            description: {
                result: required.description && description.value === '',
                message: gettext('Description is empty.').toString(),
            },
        };

        const set: {
            [key: string]: {
                helper: Dispatch<SetStateAction<Field>>;
                field: Field;
            };
        } = {
            email: {
                helper: setEmail,
                field: email,
            },
            description: {
                helper: setDescription,
                field: description,
            },
        };

        const error = validate[field];

        if (error?.result) {
            const setter = set[field];
            if (setter) {
                setter.helper({
                    ...setter.field,
                    error: error.message,
                });
            }
        } else {
            const setter = set[field];
            if (setter) {
                setter.helper({
                    ...setter.field,
                    error: '',
                });
            }
        }

        return error?.result || false;
    };

    return {
        fields: {
            email,
            description,
            required: {
                email: required.email,
                description: required.description,
            },
            userId: user?.id,
        },
        helpers: {
            validateField,
            setters: {
                setEmail,
                setDescription,
                setRequired,
            },
        },
    };
};
